import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  AfterViewInit,
} from '@angular/core';
import { NotifierService } from '../../../../_services/notifier.service';
import { MrrService } from '../api-call.service';

// ─── Models ──────────────────────────────────────────────────────────────────

export interface MrrTableHeader {
  id: string;
  name: string;
  width?: string;
}

export interface MrrTabConfig {
  id: string;
  label: string;
  icon: string;
  fetchMethod: string;
  headers: MrrTableHeader[];
  emptyMessage?: string;
}

// ─── Edit / Delete Event Payloads ─────────────────────────────────────────────

export interface MrrRowEvent {
  tabId: string;
  row: any;
  index: number;
}

export interface MrrDeleteAllEvent {
  tabId: string;
  rows: any[];
}

// ─── Dynamic Header + Tab Config Model ───────────────────────────────────────

export const MRR_TAB_CONFIGS: MrrTabConfig[] = [
  {
    id: 'mrr',
    label: 'MRR',
    icon: 'fa-table',
    fetchMethod: 'fetchMrrTab',
    headers: [
      { id: 'lid', name: 'Account ID', width: '20%' },
      { id: 'serviceName', name: 'Service Name', width: '25%' },
      { id: 'mrrDiscount', name: 'MRR Discount', width: '15%' },
      { id: 'startDate', name: 'Start Date', width: '15%' },
      { id: 'endDate', name: 'End Date', width: '15%' },
      { id: 'comment', name: 'Comment', width: '10%' },
    ],
    emptyMessage: 'No MRR entries found.',
  },
  {
    id: 'ec2',
    label: 'EC2',
    icon: 'fa-database',
    fetchMethod: 'fetchEc2Tab',
    headers: [
      { id: 'accountId', name: 'Account ID' },
      { id: 'instanceType', name: 'Instance Type' },
      { id: 'os', name: 'OS' },
      { id: 'region', name: 'Region' },
      { id: 'riType', name: 'RI Type' },
      { id: 'discountPercentage', name: 'Discount (%)' },
      { id: 'startDate', name: 'Start Date' },
      { id: 'endDate', name: 'End Date' },
    ],
    emptyMessage: 'No RI data found.',
  },
  {
    id: 's3',
    label: 'S3',
    icon: 'fa-archive',
    fetchMethod: 'fetchS3Tab',
    headers: [
      { id: 'region', name: 'Region' },
      { id: 'usageType', name: 'Usage Type' },
      { id: 'ppu', name: 'Price Per Unit' },
      { id: 'discount', name: 'Discount (%)' },
    ],
    emptyMessage: 'No S3 data found.',
  },
  {
    id: 'cfrc',
    label: 'CloudFront',
    icon: 'fa-globe',
    fetchMethod: 'fetchCfrcTab',
    headers: [
      { id: 'regionn', name: 'Region' },
      { id: 'usageType', name: 'Usage Type' },
      { id: 'ppu', name: 'Price Per Unit (USD)' },
      { id: 'discountPercentage', name: 'Discount' },
    ],
    emptyMessage: 'No CloudFront data found.',
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

@Component({
  selector: 'mrr-tabs',
  templateUrl: './discount-tab.component.html',
  styleUrls: ['./discount-tab.component.css'],
})
export class MrrTabsComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @Input() accountId: string = '';
  @Input() urlPrefix: string = 'client';
  @Input() mid: string = '';
  editTabId: string = '';

  /** Emitted when user clicks Edit on a row */
  @Output() onEdit = new EventEmitter<MrrRowEvent>();

  /** Emitted when a single row is deleted (after successful API call) */
  @Output() onDelete = new EventEmitter<MrrRowEvent>();

  /** Emitted when Delete All is confirmed and executed */
  @Output() onDeleteAll = new EventEmitter<MrrDeleteAllEvent>();

  tabs: MrrTabConfig[] = MRR_TAB_CONFIGS;
  activeTabId: string = 'mrr';

  tableData: any[] = [];
  loading: boolean = false;
  error: string | null = null;

  /** Controls the "Delete All" confirmation overlay */
  confirmDeleteAll: boolean = false;

  /** Index of the row currently being deleted (-1 = none) */
  deletingIndex: number = -1;
  isEditOpen: boolean;
  selectedRow: any;

  get activeTab(): MrrTabConfig {
    return this.tabs.find((t) => t.id === this.activeTabId) ?? this.tabs[0];
  }

  private modalEl: HTMLElement | null = null;
  private modalConfirmBtn: HTMLElement | null = null;
  private modalCancelBtn: HTMLElement | null = null;
  private modalBackdrop: HTMLElement | null = null;
  private modalCount: HTMLElement | null = null;

  // bound references so we can removeEventListener later
  private _onConfirm = () => this.onConfirmDeleteAll();
  constructor(
    private notifier: NotifierService,
    private mrrService: MrrService,
  ) {}

  ngOnInit(): void {
    console.log(this.accountId);
    if (this.accountId) {
      this.loadTab(this.activeTabId);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['accountId'] && !changes['accountId'].firstChange) {
      this.resetState();
      this.loadTab(this.activeTabId);
    }
  }

  selectTab(tabId: string): void {
    if (this.activeTabId === tabId) return;
    this.activeTabId = tabId;
    this.resetState();
    this.loadTab(tabId);
  }
  private modalElement: HTMLElement | null = null;

  ngAfterViewInit(): void {
    // intentionally empty
  }

  openDeleteConfirm(): void {
    // Remove any existing modal
    this.destroyModal();

    const count = this.tableData.length;
    const label = this.activeTab?.label ?? '';
    const plural = count !== 1 ? 's' : '';

    const backdrop = document.createElement('div');
    backdrop.id = 'mrr-delete-modal';
    backdrop.style.cssText = `
    position: fixed; inset: 0; z-index: 99999;
    background: rgba(17,24,39,0.5);
    backdrop-filter: blur(3px);
    display: flex; align-items: center; justify-content: center;
    padding: 16px;
  `;

    backdrop.innerHTML = `
    <div id="mrr-modal-dialog" style="
      background: #fff; border-radius: 14px;
      box-shadow: 0 20px 48px rgba(0,0,0,.18);
      width: 100%; max-width: 400px;
      overflow: hidden; text-align: center;
      font-family: system-ui, sans-serif;
    ">
      <div style="height:4px; background: linear-gradient(90deg,#ef4444,#f87171);"></div>
      <div style="
        width:64px; height:64px; border-radius:50%;
        background:#fef2f2; border:2px solid #fca5a5;
        display:inline-flex; align-items:center; justify-content:center;
        font-size:22px; color:#ef4444; margin:24px auto 0;
      ">🗑</div>
      <div style="padding:16px 28px 20px;">
        <h5 style="font-size:17px;font-weight:700;color:#111827;margin:0 0 8px;">Delete All Records?</h5>
        <p style="font-size:13.5px;color:#6b7280;line-height:1.65;margin:0;">
          You're about to permanently remove
          <strong style="color:#111827;">${count} ${label} record${plural}</strong>.
          This action <strong style="color:#ef4444;">cannot</strong> be undone.
        </p>
      </div>
      <div style="display:flex;gap:10px;padding:0 28px 24px;justify-content:center;">
        <button id="mrr-cancel-btn" style="
          flex:1; max-width:160px; padding:9px 18px;
          border-radius:6px; font-size:13.5px; font-weight:600;
          cursor:pointer; border:1px solid #d1d5db;
          background:#fff; color:#6b7280;
        ">Cancel</button>
        <button id="mrr-confirm-btn" style="
          flex:1; max-width:160px; padding:9px 18px;
          border-radius:6px; font-size:13.5px; font-weight:600;
          cursor:pointer; border:1px solid #ef4444;
          background:#ef4444; color:#fff;
        ">🗑 Yes, Delete All</button>
      </div>
    </div>
  `;

    document.body.appendChild(backdrop);
    this.modalElement = backdrop;

    // Backdrop click to close
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) this.destroyModal();
    });

    // Cancel
    document.getElementById('mrr-cancel-btn')!.addEventListener('click', () => {
      this.destroyModal();
    });

    // Confirm
    document
      .getElementById('mrr-confirm-btn')!
      .addEventListener('click', () => {
        console.log('✅ confirm clicked');
        this.destroyModal();
        this.deleteAll();
      });
  }

  destroyModal(): void {
    if (this.modalElement) {
      this.modalElement.remove();
      this.modalElement = null;
    }
  }

  onConfirmDeleteAll(): void {
    this.destroyModal();
    this.deleteAll();
  }

  ngOnDestroy(): void {
    this.destroyModal();
  }

  editRow(row: any, index: number): void {
    const tabIdMap: Record<string, string> = { rdsri: 'rds-ri', ec2: 'ec2' };
    this.editTabId = tabIdMap[this.activeTabId] ?? this.activeTabId;
    switch (this.activeTabId) {
      case 'mrr':
        this.selectedRow = {
          ...row,
          serviceName: row.serviceName ?? '',
          mrrDiscount: row.mrrDiscount ?? '',
          comment: row.comment ?? '',
          startDate: row.startDate ?? '',
          endDate: row.endDate ?? '',
          uuid: row.uuid ?? '',
        };
        break;

      case 's3':
        this.selectedRow = {
          ...row,
          regionn: row.region ?? row.regionn ?? '', // S3 component uses ri.regionn
          usageType: row.usageType ?? '',
          discountType: row.discountType ?? 'percentage',
          discountPercentage: row.discount ?? row.discountPercentage ?? '', // S3 reads ri.discountPercentage
          unit: row.unit ?? '',
          ppu: row.ppu ?? '',
          description: row.description ?? '',
          startDate: row.startDate ?? '',
          endDate: row.endDate ?? '',
          linkedAccounts: row.linkedAccounts ?? [],
        };
        break;

      case 'cfrc':
        this.selectedRow = {
          ...row,
          regionn: row.region ?? row.regionn ?? '', // CFRC component uses ri.regionn
          usageType: row.usageType ?? '',
          discountType: row.discountType ?? 'percentage',
          discountPercentage: row.perc ?? row.discountPercentage ?? '',
          isMrr: row.isMrr ?? false,
          startDate: row.startDate ?? '',
          endDate: row.endDate ?? '',
          linkedAccounts: row.linkedAccounts ?? [],
        };
        break;

      case 'ec2':
        this.selectedRow = {
          ...row,
          instanceType: row.instanceType ?? '',
          os: row.os ?? '',
          region: row.region ?? '',
          riType: row.riType ?? '',
          discountPercentage: row.discountPercentage ?? '',
          quantity: row.quantity ?? '',
          riCost: row.riCost ?? '',
          startDate: row.startDate ?? '',
          endDate: row.endDate ?? '',
        };
        break;

      case 'rdsri':
        this.selectedRow = {
          ...row,
          regionn: row.region ?? row.regionn ?? [], // RDS-RI also uses ri.regionn
          usageType: row.usageType ?? [],
          azType: row.azType ?? '',
          dbEngine: row.dbEngine ?? '',
          discountType: row.discountType ?? 'percentage',
          discountPercentage: row.perc ?? row.discountPercentage ?? '',
          startDate: row.startDate ?? '',
          endDate: row.endDate ?? '',
          linkedAccounts: row.linkedAccounts ?? [],
        };
        break;

      default:
        this.selectedRow = { ...row };
        break;
    }

    this.isEditOpen = true;
  }

  onEditClose(refreshType?: any): void {
    this.isEditOpen = false;
    this.selectedRow = null;
    // If refreshType is a string like 's3', map to the correct tab
    const tabToRefresh = this.resolveTabId(refreshType) ?? this.activeTabId;
    this.activeTabId = tabToRefresh;
    this.loadTab(tabToRefresh);
  }

  private resolveTabId(type: any): string | null {
    if (!type || type === true) return null;
    const map: Record<string, string> = {
      mrr: 'mrr',
      s3: 's3',
      cfrc: 'cfrc',
      ec2: 'ec2',
      'rds-ri': 'rdsri',
    };
    return map[type] ?? null;
  }

  async deleteRow(row: any, index: number): Promise<void> {
    this.deletingIndex = index;
    try {
      let result: any;

      switch (this.activeTabId) {
        case 'mrr':
          result = await this.mrrService.deleteMrr(
            row.lid ?? this.accountId,
            row.uuid,
          );
          break;

        case 's3':
          result = await this.mrrService.deleteS3(
            row.accountId ?? this.accountId,
            row.uuid,
          );
          break;

        case 'cfrc': // ← moved ABOVE default
          result = await this.mrrService.deleteCfrc(
            row.accountId ?? this.accountId,
            row.uuid,
          );
          break;

        case 'ec2':
        case 'rdsri':
          result = await this.mrrService.deleteRi(
            row.accountId ?? this.accountId,
            row.id,
          );
          break;

        default:
          result = { success: false, message: 'Unknown tab type' };
          break;
      }

      if (result?.success) {
        this.notifier.alert(
          'Success',
          '',
          'Record deleted successfully.',
          'success',
          3000,
        );
        await this.loadTab(this.activeTabId);
      } else {
        this.notifier.alert(
          'Error',
          '',
          result?.message ?? 'Failed to delete record.',
          'error',
          5000,
        );
      }
    } catch {
      this.notifier.alert(
        'Error',
        '',
        'An error occurred while deleting.',
        'error',
        5000,
      );
    } finally {
      this.deletingIndex = -1;
    }
  }

  testClick(event: any) {
    console.log('🔥 CLICK WORKING');
  }

  async deleteAll() {
    console.log('🚀 deleteAll triggered');

    this.confirmDeleteAll = false;

    // ✅ Map component tab IDs to backend tableType strings
    const tabTypeMap: Record<string, string> = {
      ec2: 'ri',
      rdsri: 'rds-ri',
      mrr: 'mrr',
      s3: 's3',
      cfrc: 'cfrc',
    };

    const tableType = tabTypeMap[this.activeTabId] ?? this.activeTabId;
    console.log('Table Type (mapped):', tableType);

    this.loading = true;

    try {
      const res = await this.mrrService.deleteAll(this.accountId, tableType);
      console.log('API Response:', res);

      if (res.success) {
        this.tableData = [];
        this.loadTab(this.activeTabId);
      } else {
        console.error('❌ API returned failure:', res.message);
        this.notifier.alert(
          'Error',
          '',
          res.message ?? 'Failed to delete all records.',
          'error',
          5000,
        );
      }
    } catch (e) {
      console.error('❌ Delete all failed with error:', e);
      this.notifier.alert('Error', '', 'An error occurred.', 'error', 5000);
    } finally {
      this.loading = false;
    }
  }

  // ─── Load Tab ─────────────────────────────────────────────────────────────

  async loadTab(tabId: string): Promise<void> {
    if (!this.accountId) return;

    this.loading = true;
    this.error = null;
    this.tableData = [];

    const tab = this.tabs.find((t) => t.id === tabId);
    if (!tab) {
      this.loading = false;
      return;
    }

    const methodName = tab.fetchMethod as keyof MrrService;
    const fetchFn = (this.mrrService as any)[methodName];

    if (typeof fetchFn !== 'function') {
      this.error = `Service method "${tab.fetchMethod}" is not implemented yet.`;
      this.loading = false;
      return;
    }

    console.log('Calling API for tab:', tabId);

    const result = await fetchFn.call(this.mrrService, {
      accountId: this.accountId,
      urlPrefix: this.urlPrefix,
      mid: this.mid,
    });

    if (result.success) {
      this.tableData = result.data ?? [];
    } else {
      this.error = result.message ?? 'Failed to load data.';
      this.tableData = [];
    }

    this.loading = false;
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  private resetState(): void {
    this.tableData = [];
    this.error = null;
    this.confirmDeleteAll = false;
    this.deletingIndex = -1;
  }

  /** Capitalises the first letter of a string */
  private capitalise(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /** Formats a cell value based on its column id */
  formatCell(value: any, headerId: string): string {
    if (value === null || value === undefined || value === '') return '-';
    if (headerId === 'perc' || headerId === 'mrrDiscount') return `${value}%`;
    if (typeof value === 'boolean') return value ? '✓ Yes' : '✗ No';
    return String(value);
  }

  /** Returns CSS class for a cell based on value and column */
  cellClass(value: any, headerId: string): string {
    if (headerId === 'isMrr') {
      return value === true ? 'badge-applied' : 'badge-not-applied';
    }
    if (headerId === 'perc' || headerId === 'mrrDiscount') {
      return 'text-discount';
    }
    return '';
  }
}
